import { QueryKey, UseMutationResult, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { differenceInSeconds, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import type { NavGroup, NavLink, VerticalNavItemsType } from 'src/@core/layouts/types'
import { type CapabilityWithPage } from 'src/atoms'
import authConfig from 'src/configs/auth'
import type { GeneralPage, Module, Role, User } from 'src/types/directus/current-user'
import client from '@/client'
import { FetchParametersOptions, GetSelectResponse } from '@/components/molecules/select-async'
import { useCurrentAction } from '@/components/templates/page-primary/hooks/stores/useCurrentAction'
import { PREFIX_KEY } from '@/constant/common'
import { useAlert } from '@/hooks/stores/useAlert'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { queryClient } from '@/utils/queryClient'

type GetPageCapabilityParams = {
  capabilities: CapabilityWithPage[]
  pathname: string
}

export function getPageCapabilities(params: GetPageCapabilityParams) {
  return params.capabilities.filter(capability => capability.page.url === params.pathname)
}

type GetNavItemsParams = {
  data: User | null
  roleIds: Role['id'][]
  selectedRoleId: Role['id']
  uiConfigs: {
    code: string
    value: string
  }[]
  superAdminRoleCodes?: string[]
  pathname?: string
}

type ModifiedModule = Module & {
  additional_type: 'module'
}

type ModifiedPage = GeneralPage & {
  additional_type: 'page'
}

const isModifiedModule = (mod: ModifiedModule | ModifiedPage): mod is ModifiedModule => mod.additional_type === 'module'

export const getNavItems = (params: GetNavItemsParams) => {
  const selectedRole = params.data?.roles.find(role => role.id === params.selectedRoleId)
  if (!selectedRole || !Array.isArray(selectedRole.products) || selectedRole.products.length === 0) {
    return []
  }

  const superAdminRoleCodes = params.superAdminRoleCodes || authConfig.superAdminRoleCodes
  const isSuperAdmin = superAdminRoleCodes.includes(selectedRole.code)

  const currentPath = params.pathname || (typeof window !== 'undefined' ? window.location.pathname : '')

  // 1. Resolve active product dynamically based on current pathname
  let activeProduct = null

  if (currentPath && currentPath !== '/') {
    activeProduct = selectedRole.products.find(product => {
      // A. Match by page URL within product
      const hasMatchingPage =
        product.modules?.some(mod =>
          mod.clusters?.some(cluster => cluster.pages?.some(page => page.url && currentPath.startsWith(page.url)))
        ) || product.generalPages?.some(page => page.url && currentPath.startsWith(page.url))

      if (hasMatchingPage) return true

      // B. Match by product route prefix: /solutions/<productCode> or /<productCode>
      const normalizedCode = product.code?.toLowerCase()
      if (normalizedCode) {
        if (currentPath.startsWith(`/solutions/${normalizedCode}`) || currentPath.startsWith(`/${normalizedCode}`)) {
          return true
        }

        // Special routing alias (ADMIN product has base path /solutions/core or /core)
        if (
          (normalizedCode === 'admin' || normalizedCode === 'core') &&
          (currentPath.startsWith('/solutions/core') || currentPath.startsWith('/core'))
        ) {
          return true
        }
      }

      return false
    })
  }

  // 2. Fallback to first matching published product
  if (!activeProduct) {
    activeProduct =
      selectedRole.products.find(product => product.status === 'published' || isSuperAdmin) || selectedRole.products[0]
  }

  if (!activeProduct) {
    return []
  }

  const modules = (activeProduct.modules || [])
    .filter(mod => mod.status === 'published')
    .map<ModifiedModule>(mod => {
      const clusters = mod.clusters
        .filter(cluster => cluster.status === 'published')
        .map(cluster => {
          const pages = cluster.pages
            .sort((a, b) => {
              if (a.order === null || a.order === undefined) {
                return 1
              }

              if (b.order === null || b.order === undefined) {
                return -1
              }

              return a.order - b.order
            })
            .filter(page => {
              if (isSuperAdmin) {
                return page.status === 'published' && page.type !== 'sub-page'
              }

              const relatedCapability = page.capabilities.find(capability => {
                return (
                  capability.role === selectedRole.id &&
                  capability.page === page.id &&
                  capability.status === 'published'
                )
              })

              const hasCapability = !!relatedCapability

              if (page.status !== 'published' || page.type === 'sub-page' || !hasCapability) {
                return false
              }

              return true
            })

          return {
            ...cluster,
            pages
          }
        })
        .sort((a, b) => {
          if (a.order === null || a.order === undefined) {
            return 1
          }

          if (b.order === null || b.order === undefined) {
            return -1
          }

          return a.order - b.order
        })
        .filter(cluster => cluster.pages.length)

      return {
        ...mod,
        clusters,
        additional_type: 'module'
      }
    })
    .filter(mod => mod.clusters.length)

  const generalPages = (activeProduct.generalPages || [])
    .map<ModifiedPage>(page => ({
      ...page,
      additional_type: 'page'
    }))
    .filter(page => {
      if (isSuperAdmin) {
        return page.status === 'published' && page.type !== 'sub-page'
      }

      const relatedCapability = page.capabilities.find(capability => {
        return capability.role === selectedRole.id && capability.page === page.id && capability.status === 'published'
      })

      const hasCapability = !!relatedCapability

      if (page.status !== 'published' || page.type === 'sub-page' || !hasCapability) {
        return false
      }

      return true
    })

  const modulesAndPages: (ModifiedModule | ModifiedPage)[] = [...modules, ...generalPages].sort((before, after) => {
    if (before.order === null || before.order === undefined) {
      return 1
    }

    if (after.order === null || after.order === undefined) {
      return -1
    }

    return before.order - after.order
  })

  const navItems = modulesAndPages.map<VerticalNavItemsType[number]>(mod => {
    if (isModifiedModule(mod)) {
      const navItem: NavGroup = {
        title: mod.name
      }

      if (mod.icon) {
        navItem.icon = mod.icon
      }

      navItem.children = mod.clusters
        .map(cluster => {
          const pages = cluster.pages.map(page => {
            const regex = /\[([^\]]+)\]/g
            const matches = page.name.match(regex)
            let title = page.name
            if (matches) {
              const codes = params.uiConfigs.map(item => item.code)

              title = page.name.replace(/\[([^\]]+)\]/g, (match, p1) => {
                const codeIndex = codes.indexOf(p1)

                return codeIndex !== -1 ? params.uiConfigs[codeIndex].value : match
              })
            }

            const subNavItem: NavLink = {
              title,
              path: page.is_external_src ? `/embed/${page.id}` : page.url
            }

            if (page.icon) {
              subNavItem.icon = page.icon
            }

            return subNavItem
          })

          return [
            {
              sectionTitle: cluster.name
            },
            ...pages
          ]
        })
        .flat()

      return navItem
    }

    return {
      title: mod.name,
      path: mod.is_external_src ? `/embed/${mod.id}` : mod.url,
      icon: mod.icon
    }
  })

  return navItems
}

export function isValidHttpUrl(urlString: string) {
  let url

  try {
    url = new URL(urlString)
  } catch {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

export function titleCase(str: string) {
  return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())
}

export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes'

  const k = 1000
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Helper function to streamline the creation of mutation hooks with React Query's `useMutation`.
 * This function abstracts common success and error handling patterns, including displaying alert notifications
 * and setting the duration for those alerts. It simplifies the process of defining mutations by encapsulating
 * alert logic, query invalidation, and error handling in one place.
 *
 * @param {function} mutationFn - The mutation function that performs the asynchronous operation (e.g., API call).
 * @param {string} primarySuccessMessage - The primary success message to be shown when the mutation succeeds.
 * @param {string} secondarySuccessMessage - The secondary success message to be shown when the mutation succeeds.
 * @param {string} primaryErrorMessage - The primary error message to be shown when the mutation fails.
 * @param {string} secondaryErrorMessage - The secondary error message to be shown when the mutation fails.
 * @param {string} detinationPathname - The destination pathname when the mutation succeeds.
 * @param {number} [durationAlert=5000] - The duration for which the alert should be displayed (in milliseconds). Default is 5000ms.
 *
 * @returns {UseMutationResult} - Returns the result of the mutation, including status, data, error, and functions for executing the mutation.
 */
export function useMutationHelper(
  mutationFn: (payload: any) => Promise<any>,
  primarySuccessMessage: string,
  secondarySuccessMessage: string,
  primaryErrorMessage: string,
  secondaryErrorMessage: string,
  detinationPathname: string,
  durationAlert?: number,
  mode?: 'add' | 'edit' | 'delete',
  onSuccessAddition?: (data?: any, variables?: any) => void,
  onErrorAddition?: any,
  customQueryKeys?: QueryKey[]
): UseMutationResult<any, any, any> {
  const navigate = useNavigate()
  const { alert, setAlert } = useAlert()
  const { setCurrentActionGlobal } = useCurrentAction()

  const filterResult = sessionStorage.getItem('filterResult')

  const dynamicDestinationPathname =
    mode === 'edit' || mode === 'delete' ? `${detinationPathname}?filterResult=${filterResult}` : detinationPathname

  const isOnline = useOnlineStatus()

  const duration = durationAlert || 5000

  return useMutation({
    mutationFn,
    onSuccess: async (response: any, variables: any) => {
      if (customQueryKeys?.length) {
        await Promise.all(
          customQueryKeys.map(queryKey =>
            queryClient.invalidateQueries({
              queryKey
            })
          )
        )
      } else {
        await queryClient.invalidateQueries()
      }
      detinationPathname && navigate(dynamicDestinationPathname)

      setAlert({
        title: primarySuccessMessage,
        content: secondarySuccessMessage,
        size: 'small',
        variant: 'success',
        open: true,
        color: 'success',
        icon: 'ic:baseline-check',
        pathname: detinationPathname,
        actionType: mode
      })

      setCurrentActionGlobal(null)

      setTimeout(() => {
        setAlert({
          ...alert,
          open: false
        })
      }, duration)

      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 100)

      onSuccessAddition?.(response, variables)
    },
    onError: (error: any) => {
      const errorMessage =
        secondaryErrorMessage || error.response?.data?.message || error.message || 'An error occurred.'

      setAlert({
        title: primaryErrorMessage,
        content: isOnline ? errorMessage : 'Unable to connect to the network or server.',
        size: 'small',
        variant: 'danger',
        open: true,
        color: 'error',
        icon: 'ic:baseline-do-disturb',
        actionType: mode
      })

      setTimeout(() => {
        setAlert({
          ...alert,
          open: false
        })
      }, duration)

      onErrorAddition?.(error)
    }
  })
}

/**
 * Helper function to fetch data list using React Query.
 * This function abstracts common logic for data fetching with GET requests.
 *
 * @template TResponse - The expected shape of the response data.
 * @param {string} queryPrefix - Prefix for the query key, e.g., 'get' or 'fetch'.
 * @param {string} primaryKey - The primary query key for caching purposes, e.g., 'company', 'shift'.
 * @param {string} url - The API endpoint URL to fetch the data from.
 * @param {Record<string, any>} [params] - Optional parameters to be sent as query parameters in the request.
 * @returns {UseQueryResult<TResponse>} - React Query's query result containing the data, loading state, and errors.
 */
export function useQueryListHelper<TResponse>(
  queryPrefix: string,
  primaryKey: string,
  url: string,
  params?: Record<string, any>,
  customBaseUrl?: any
) {
  const queryKeys: QueryKey = [queryPrefix, primaryKey, params ? JSON.stringify(params) : {}]

  return useQuery<TResponse, Error>({
    queryKey: queryKeys,
    async queryFn() {
      const response = await client.api.get<TResponse>(url, {
        params: params || {},
        baseURL: customBaseUrl || undefined
      })

      return response.data
    }
  })
}

export const useInfiniteQueryOptionsHelper = (
  queryKeyPrefix: string,
  url: string,
  renderParamsFn: (params?: FetchParametersOptions, page?: { pageParam?: number }) => any,
  params?: FetchParametersOptions
): any => {
  const queryKeys = [PREFIX_KEY.GET, queryKeyPrefix, params ? renderParamsFn(params) : {}]

  return useInfiniteQuery<GetSelectResponse, Error>({
    queryKey: queryKeys,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const currPageParam = pageParam || 1
      const queryParams = params ? renderParamsFn(params, currPageParam) : {}

      const response = await client.api.get<GetSelectResponse>(url, {
        params: queryParams
      })

      return response.data
    },
    getNextPageParam: lastPage => {
      const nextPage = (lastPage.meta?.current_page ?? 1) + 1

      return nextPage <= (lastPage.meta?.total_page ?? 1) ? nextPage : undefined
    }
  })
}

// Helper func deep comparison arrays of object
export const isArrayEqual = (arr1: any[], arr2: any[]) => {
  if (!arr1 || !arr2) return

  if (arr1.length !== arr2.length) return false

  const arr1Sorted = [...arr1].sort()
  const arr2Sorted = [...arr2].sort()

  return arr1Sorted.every((value, index) => JSON.stringify(value) === JSON.stringify(arr2Sorted[index]))
}

// Helper func simplify wording
export const getSimpleWordingHelper = (name: string, maxLength: number = 20) => {
  if (!name) return
  if (name.length < maxLength) {
    return name
  }

  return `${name.slice(0, maxLength)}...`
}

/**
 * Helper to compare two arrays of boolean values
 * @param updated - Array of updated boolean values
 * @param initial - Array of initial boolean values
 * @returns boolean - true if all values are identical, false otherwise
 */
export function areArraysIdentical(updated: boolean[], initial: boolean[]): boolean {
  // Ensure both arrays are of the same length
  if (updated.length !== initial.length) {
    return false
  }

  // Compare each value in the arrays
  return updated.every((value, index) => value === initial[index])
}

/**
 * Utility to fetch the saved role from localStorage and set it in the state
 * if it's not already set.
 *
 */
export const currentSelectedRole =
  typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('selectedRole') || 'null') : null

/**
 * Calculates the time difference between two ISO date strings and formats it as HH:MM:SS.
 * @param start - The start time in ISO string format.
 * @param end - The end time in ISO string format.
 * @returns The formatted time difference as HH:MM:SS.
 */
export const formatTimeDifference = (start: string, end: string): string => {
  // Parse the ISO strings into Date objects
  const startTime = parseISO(start)
  const endTime = parseISO(end)

  // Calculate the total difference in seconds
  const totalSeconds = differenceInSeconds(endTime, startTime)

  // Calculate hours, minutes, and seconds
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  // Format as HH:MM:SS
  const pad = (num: number) => String(num).padStart(2, '0')

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export function handleFormatStartEndDateTime({ date, typeDate }: { date: Date | string; typeDate: 'start' | 'end' }) {
  const formattedDate = typeof date === 'string' ? new Date(date) : date
  const year = formattedDate.getFullYear()
  const month = formattedDate.getMonth()
  const day = formattedDate.getDate()

  if (typeDate === 'end') {
    const formattedEndDate = new Date(Date.UTC(year, month, day, 23, 59, 59))

    return formattedEndDate
  }

  const formattedStartDate = new Date(Date.UTC(year, month, day, 0, 0, 0))

  return formattedStartDate
}
